import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, IconButton, Tooltip,
  TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Alert, Divider, Stack, Avatar, Button,
  ThemeProvider, createTheme, CssBaseline, useMediaQuery,
} from '@mui/material';
import {
  Search, Refresh, OpenInNew, PictureAsPdf, Person, Logout,
  AssignmentLate, CheckCircle, HourglassEmpty, Schedule, Description,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#0a0f1e', paper: '#111827' },
    primary: { main: '#3b82f6' },
    secondary: { main: '#8b5cf6' },
  },
  typography: { fontFamily: '"Satoshi", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' },
});

const STATUS_CONFIG = {
  completed: { label: 'Report Ready', color: 'success', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  in_progress: { label: 'In Progress', color: 'warning', icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
  claimed: { label: 'Assigned', color: 'info', icon: <Schedule sx={{ fontSize: 14 }} /> },
  requested: { label: 'Pending', color: 'default', icon: <AssignmentLate sx={{ fontSize: 14 }} /> },
  not_requested: { label: 'Not Requested', color: 'default', icon: <Description sx={{ fontSize: 14 }} /> },
};

function StatusChip({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_requested;
  return (
    <Chip
      icon={cfg.icon}
      label={cfg.label}
      color={cfg.color}
      size="small"
      variant="outlined"
      sx={{ fontSize: '0.72rem' }}
    />
  );
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatPatient(patient, order) {
  if (patient?.firstName) return `${patient.lastName}, ${patient.firstName}`;
  if (order.patientName) return order.patientName;
  return '—';
}

export default function ReferringPhysicianPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');

  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchStudies = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/studies/referring-physician/my-studies');
      setStudies(res.data.studies || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load referred studies.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStudies(); }, [fetchStudies]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filtered = studies.filter(s => {
    const patientStr = formatPatient(s.patient, s).toLowerCase();
    const acc = (s.accessionNumber || '').toLowerCase();
    const proc = (s.procedureName || s.studyDescription || '').toLowerCase();
    const q = search.toLowerCase();
    const matchSearch = !q || patientStr.includes(q) || acc.includes(q) || proc.includes(q);
    const matchStatus = statusFilter === 'all' || s.reportStatus === statusFilter;
    return matchSearch && matchStatus;
  });

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const completedCount = studies.filter(s => s.reportStatus === 'completed').length;
  const pendingCount = studies.filter(s => ['requested', 'claimed', 'in_progress'].includes(s.reportStatus)).length;

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>

        {/* Top bar */}
        <Box sx={{
          px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          background: 'linear-gradient(135deg, #0d1b4b 0%, #111827 100%)',
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 1,
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>D</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="white" lineHeight={1.2}>
                DycoVue Results Portal
              </Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.5)">
                Referring Physician Access
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#3b82f6', fontSize: 14 }}>
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </Avatar>
            {!isMobile && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="white" fontWeight={600} lineHeight={1.2}>
                  Dr. {user?.firstName} {user?.lastName}
                </Typography>
                <Typography variant="caption" color="rgba(255,255,255,0.5)">
                  {user?.specialty || 'Referring Physician'}
                </Typography>
              </Box>
            )}
            <Tooltip title="Sign Out">
              <IconButton onClick={handleLogout} size="small" sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ef4444' } }}>
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Main content */}
        <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 4 }, py: 3, maxWidth: 1400, mx: 'auto', width: '100%' }}>

          {/* Summary row */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
            {[
              { label: 'Total Referrals', value: studies.length, color: '#3b82f6' },
              { label: 'Reports Ready', value: completedCount, color: '#22c55e' },
              { label: 'Awaiting Report', value: pendingCount, color: '#f59e0b' },
            ].map(card => (
              <Paper key={card.label} sx={{
                flex: 1, p: 2, borderRadius: 2,
                border: `1px solid ${card.color}30`,
                background: `linear-gradient(135deg, ${card.color}10, transparent)`,
              }}>
                <Typography variant="h4" fontWeight={800} sx={{ color: card.color }}>{card.value}</Typography>
                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
              </Paper>
            ))}
          </Stack>

          {/* Filters */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems="center">
            <TextField
              placeholder="Search patient, accession, procedure…"
              size="small"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              sx={{ flex: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            />
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={e => { setStatusFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="completed">Report Ready</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="claimed">Assigned</MenuItem>
                <MenuItem value="requested">Pending</MenuItem>
                <MenuItem value="not_requested">Not Requested</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchStudies} disabled={loading} size="small">
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper sx={{ borderRadius: 2, border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: 'rgba(255,255,255,0.04)', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 } }}>
                      <TableCell>Patient</TableCell>
                      {!isMobile && <TableCell>Accession</TableCell>}
                      <TableCell>Modality / Procedure</TableCell>
                      {!isMobile && <TableCell>Order Date</TableCell>}
                      {!isMobile && <TableCell>Study Date</TableCell>}
                      <TableCell>Report Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginated.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                          <Person sx={{ fontSize: 48, mb: 1, opacity: 0.3, display: 'block', mx: 'auto' }} />
                          {studies.length === 0
                            ? 'No referred studies found. Studies appear here once orders list you as the referring physician.'
                            : 'No studies match your search.'}
                        </TableCell>
                      </TableRow>
                    ) : paginated.map(study => (
                      <TableRow
                        key={study._id}
                        hover
                        sx={{
                          '& td': { py: '3px !important', px: '8px !important', fontSize: '0.75rem', height: '34px' },
                          '&:hover': { bgcolor: 'rgba(59,130,246,0.04)' }
                        }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box component="span" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                              {formatPatient(study.patient, study)}
                            </Box>
                            {study.patient?.patientId && (
                              <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                #{study.patient.patientId}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                              {study.accessionNumber || '—'}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip label={study.modality || '?'} size="small" sx={{ fontSize: '0.7rem', fontWeight: 700 }} />
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {study.procedureName || study.studyDescription || '—'}
                            </Typography>
                          </Stack>
                          {study.clinicalIndication && (
                            <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 220 }}>
                              {study.clinicalIndication}
                            </Typography>
                          )}
                        </TableCell>
                        {!isMobile && <TableCell><Typography variant="caption">{formatDate(study.orderDate)}</Typography></TableCell>}
                        {!isMobile && <TableCell><Typography variant="caption">{formatDate(study.studyDate)}</Typography></TableCell>}
                        <TableCell><StatusChip status={study.reportStatus} /></TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            {study.reportStatus === 'completed' && study.reportId && (
                              <Tooltip title="View Report PDF">
                                <IconButton
                                  size="small"
                                  sx={{ color: '#22c55e' }}
                                  onClick={() => window.open(`/secure-pdf/${study.reportId}`, '_blank')}
                                >
                                  <PictureAsPdf fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {study.hasImages && study.studyId && (
                              <Tooltip title="View Images">
                                <IconButton
                                  size="small"
                                  sx={{ color: '#3b82f6' }}
                                  onClick={() => window.open(`/secure-viewer/${study.studyId}`, '_blank')}
                                >
                                  <OpenInNew fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider />
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filtered.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
              />
            </Paper>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ py: 1.5, textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <Typography variant="caption" color="rgba(255,255,255,0.25)">
            DycoVue — Read-only referring physician access. For queries, contact your radiology department.
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
